import type {
  HTMLAttributes,
  MouseEvent as ReactMouseEvent,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react'
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import {
  TABLE_HEADER_HEIGHT_CLASS,
  TABLE_HEADER_TEXT_CLASS,
  TABLE_ROW_HEIGHT_CLASS,
} from '@/components/shared/DataTable/table-dimensions'
import { cn } from '@/lib/utils'

// Rows inside thead take the header height, rows inside tbody the one row
// height (design audit S10, compact-rows feedback 2026-09-10).
const TableSectionContext = createContext<'head' | 'body'>('body')

const Table = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      // Marks a table rendered by this primitive so table-styles.css can
      // enforce the 40 px row against per-page cell padding utilities.
      data-table=""
      // w-full: a table fills its card or section like every other block;
      // without it the browser shrink-wraps it to its content and leaves an
      // empty band on the right. A caller's own width class still wins.
      className={cn('text-body w-full caption-bottom', className)}
      {...props}
    />
  ),
)
Table.displayName = 'Table'

const TableHeader = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, children, ...props }, ref) => (
  <thead ref={ref} className={cn('border-none', className)} {...props}>
    <TableSectionContext.Provider value="head">
      {children}
    </TableSectionContext.Provider>
  </thead>
))
TableHeader.displayName = 'TableHeader'

const TableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('', className)} {...props} />
))
TableBody.displayName = 'TableBody'

const TableFooter = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t bg-primary/5 font-medium dark:bg-primary/10',
      className,
    )}
    {...props}
  />
))
TableFooter.displayName = 'TableFooter'

const TableRow = forwardRef<
  HTMLTableRowElement,
  HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => {
  const section = useContext(TableSectionContext)
  return (
    <tr
      ref={ref}
      className={cn(
        'relative border-b border-border',
        section === 'head' ? TABLE_HEADER_HEIGHT_CLASS : TABLE_ROW_HEIGHT_CLASS,
        className,
      )}
      {...props}
    />
  )
})
TableRow.displayName = 'TableRow'

interface TableHeadProps extends Omit<
  ThHTMLAttributes<HTMLTableCellElement>,
  'onResize'
> {
  isResizable?: boolean
  onResize?: (width: number) => void
}

const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, isResizable, onResize, style, ...props }, ref) => {
    const [isResizing, setIsResizing] = useState(false)
    const thRef = useRef<HTMLTableCellElement | null>(null)
    const startXRef = useRef<number>(0)
    const startWidthRef = useRef<number>(0)
    const resizerRef = useRef<HTMLDivElement | null>(null)

    useImperativeHandle(ref, () => thRef.current as HTMLTableCellElement)

    const handleResizerMouseDown = useCallback(
      (e: ReactMouseEvent<HTMLDivElement>) => {
        if (!thRef.current || !isResizable) return

        e.preventDefault()
        e.stopPropagation()

        const columnId = thRef.current.getAttribute('data-column')
        if (columnId === 'view') return

        setIsResizing(true)
        startXRef.current = e.clientX
        startWidthRef.current = thRef.current.offsetWidth

        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'

        const handleMouseMove = (e: MouseEvent) => {
          if (!thRef.current) return

          if (columnId === 'view') return

          const widthChange = e.clientX - startXRef.current
          const newWidth = startWidthRef.current + widthChange
          const minWidth = 40
          const finalWidth = Math.max(newWidth, minWidth)

          if (onResize) onResize(finalWidth)

          if (resizerRef.current) {
            resizerRef.current.classList.add('active')
          }
        }

        const handleMouseUp = () => {
          setIsResizing(false)
          document.body.style.removeProperty('cursor')
          document.body.style.removeProperty('user-select')

          if (resizerRef.current) {
            resizerRef.current.classList.remove('active')
          }

          document.removeEventListener('mousemove', handleMouseMove)
          document.removeEventListener('mouseup', handleMouseUp)
        }

        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
      },
      [isResizable, onResize],
    )

    return (
      <th
        ref={thRef}
        className={cn(
          // One table header token (design audit S10)
          TABLE_HEADER_HEIGHT_CLASS,
          TABLE_HEADER_TEXT_CLASS,
          // nowrap: a label that wraps ("Effective / Date") makes the whole
          // header row taller than its 36px, which reads as a padded bar.
          'relative select-none whitespace-nowrap border-r border-border/15 bg-background px-3 py-0 text-left align-middle last:border-r-0',
          isResizing && 'opacity-70',
          className,
        )}
        style={style}
        {...props}
      >
        {props.children}

        {isResizable && (
          <div
            ref={resizerRef}
            className="column-resizer"
            onMouseDown={handleResizerMouseDown}
            onClick={e => e.stopPropagation()}
            aria-hidden="true"
            title="Drag to resize column"
          />
        )}
      </th>
    )
  },
)
TableHead.displayName = 'TableHead'

const TableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(({ className, style, ...props }, ref) => {
  return (
    <td
      ref={ref}
      className={cn(
        // Cells clip when they run out of room; no ellipsis. A td-level
        // ellipsis renders a stray "..." next to icon-only content
        // (actions menus, badges), so overflow is cut cleanly instead.
        'overflow-hidden text-clip border-r border-border/15 px-3 py-0 align-middle text-foreground last:border-r-0',
        className,
      )}
      style={style}
      {...props}
    />
  )
})
TableCell.displayName = 'TableCell'

const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn('text-caption mt-4', className)}
    {...props}
  />
))
TableCaption.displayName = 'TableCaption'

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
